import type { AuditChanges, AuditLogEntry } from '@inou/shared';

import { EntityLink } from '@/components/nav/EntityLink';
import { Badge } from '@/components/ui/badge';
import { Table, TableWrap, Td, Th } from '@/components/ui/table';
import {
  type NavTarget,
  toAssignments,
  toMember,
  toProject,
  toProjects,
  toTenantSettings,
  toTimeLogs,
} from '@/lib/nav';

/**
 * 監査ログの表。
 *
 * 1 行が表すのは「誰が・いつ・どの対象に・どの操作をしたか」まで。
 * before / after は差分表として、detail は平のキー/値として描く
 * (「200 が新しい単価なのか件数なのか」を読み手が判別できるようにするため、
 *  サーバー側で 2 つのキーに分けてある)。
 */

/**
 * action の日本語ラベル。**表に無い action はそのまま出す** —
 * サーバー側が先に増えても画面が壊れないようにする
 * (action の集合の正は `apps/api/src/lib/audit.ts`)。
 */
const ACTION_LABELS: Record<string, string> = {
  'time_log.update': '工数を訂正',
  'time_log.delete': '工数を削除',
  'user.invite': 'メンバーを招待',
  'user.update': 'メンバーを変更',
  'user.delete': '招待を取り消し',
  'user.invitation_reissue': '招待を再発行',
  'project.create': 'プロジェクトを作成',
  'project.update': 'プロジェクトを変更',
  'project.repository_add': 'リポジトリを紐付け',
  'project.repository_remove': 'リポジトリの紐付けを解除',
  'project_type.create': 'プロジェクト種別を作成',
  'project_type.update': 'プロジェクト種別を変更',
  'project_type.delete': 'プロジェクト種別を削除',
  'assignment.create': 'アサインを作成',
  'assignment.update': 'アサインを変更',
  'assignment.delete': 'アサインを削除',
  'assignment.copy': 'アサインを複製',
  'tenant.settings_update': 'テナント設定を変更',
  'tenant.ip_allowlist_add': 'IP 許可リストに追加',
  'tenant.ip_allowlist_remove': 'IP 許可リストから削除',
  'tenant.export': 'データをエクスポート',
  'api_token.create': 'MCP トークンを発行',
  'api_token.revoke': 'MCP トークンを失効',
  'oauth_grant.create': 'MCP クライアントを接続',
  'oauth_grant.revoke': 'MCP クライアントの接続を解除',
  'tenant.create': 'テナントを開通',
  'tenant.hq_update': 'テナントの状態・枠を変更',
  'tenant.admin_invitation_reissue': '管理者の招待を再発行',
  'tenant.revoke_sessions': 'セッションを一括失効',
};

/** changes のキーの日本語ラベル。表に無いキーはそのまま出す。 */
const FIELD_LABELS: Record<string, string> = {
  project_id: 'プロジェクト',
  user_id: 'メンバー',
  hours: '時間',
  worked_on: '作業日',
  category: 'カテゴリ',
  is_billable: '請求可能',
  is_capitalized_log: '資産化',
  email: 'メールアドレス',
  name: '名前',
  role: 'ロール',
  status: '状態',
  hourly_cost: '原価単価',
  hourly_rate: '売上単価',
  expires_at: '有効期限',
  is_capitalizable: '資産化の対象',
  project_type_id: '種別',
  budget_mode: '予算の時間軸',
  mcp_custom_instruction: 'MCP の記録ルール',
  repository: 'リポジトリ',
  default_billable: '請求可能の既定',
  counts_as_direct: '直接工数として数える',
  month: '対象月',
  planned_hours: '予定工数',
  custom_hourly_cost: '特別原価単価',
  note: 'メモ',
  from: '複製元',
  to: '複製先',
  copied: '複製した件数',
  skipped: '飛ばした件数',
  timezone: 'タイムゾーン',
  default_hourly_cost: '既定の原価単価',
  default_hourly_rate: '既定の売上単価',
  mfa_policy: '二段階認証の範囲',
  cidr: '範囲',
  label: 'ラベル',
  granted_full_seats: '付与フル枠',
  granted_mcp_seats: '付与 MCP 枠',
  feature_flags: '機能フラグ',
  deletion_scheduled_at: '削除予定日',
  include_tokens: 'PAT も失効',
  sessions: '失効したセッション',
  tokens: '失効した PAT',
};

function fieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? key;
}

function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

/** 値の見せ方。数値・識別子は等幅で出す。 */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '未設定';
  if (typeof value === 'boolean') return value ? 'あり' : 'なし';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function timestamp(seconds: number): string {
  return new Date(seconds * 1000).toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * 対象の飛び先。**開ける人にだけ**リンクになる (`EntityLink` が判定する)。
 * 対象が消えている行 (削除の記録) でも一覧までは飛べるようにしておく。
 */
function targetOf(log: AuditLogEntry): NavTarget | null {
  switch (log.targetType) {
    case 'project':
      return log.targetId
        ? toProject({ id: log.targetId, name: log.targetLabel ?? '' })
        : toProjects('');
    case 'user':
      return log.targetId ? toMember({ id: log.targetId, name: log.targetLabel ?? '' }) : null;
    case 'time_log': {
      // 工数明細は月で開く。差分に載っている作業日から月を取る。
      const worked =
        (log.changes?.after?.['worked_on'] as string | undefined) ??
        (log.changes?.before?.['worked_on'] as string | undefined);
      return toTimeLogs(worked ? { month: worked.slice(0, 7) } : {});
    }
    case 'assignment': {
      const month =
        (log.changes?.after?.['month'] as string | undefined) ??
        (log.changes?.before?.['month'] as string | undefined) ??
        (log.changes?.detail?.['to'] as string | undefined);
      return toAssignments(month ? { month } : {});
    }
    case 'project_type':
      // 種別そのものの画面は無いので、その種別で絞ったプロジェクト一覧へ送る
      return toProjects(log.targetLabel ?? '');
    case 'tenant':
      return toTenantSettings();
    default:
      // api_token / oauth_grant は本人の画面 (MCP接続) にしかないので飛ばさない
      return null;
  }
}

/**
 * 変更内容。差分と、行の状態ではない事実を分けて描く。
 *
 * **片側だけの項目は矢印を出さない。** 作成は after だけ、削除は before だけを
 * 持つので、両側そろっている項目と同じ「旧 → 新」で描くと、無い側が
 * 「未設定」として出てしまう (作成が「未設定 → 名前」に見える)。
 * 判定は `Object.hasOwn` — `undefined` で見ると、値としての `null`
 * (既定単価を空に戻した等) を「無い」と取り違える。
 */
function Changes({ changes }: { changes: AuditChanges | null }) {
  if (changes === null) return <span className="text-ink-subtle">{'—'}</span>;

  const before = changes.before ?? {};
  const after = changes.after ?? {};
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  const detail = Object.entries(changes.detail ?? {});

  return (
    <div className="flex flex-col gap-1">
      {keys.map((key) => {
        const hasBefore = Object.hasOwn(before, key);
        const hasAfter = Object.hasOwn(after, key);
        return (
          <div key={key} className="flex flex-wrap items-baseline gap-x-2 text-xs">
            <span className="text-ink-muted">{fieldLabel(key)}</span>
            {hasBefore ? (
              <span
                className={
                  hasAfter ? 'font-mono text-ink-subtle line-through' : 'font-mono text-ink-subtle'
                }
              >
                {formatValue(before[key])}
              </span>
            ) : null}
            {hasBefore && hasAfter ? <span className="text-ink-subtle">{'→'}</span> : null}
            {hasAfter ? (
              <span className="font-mono text-brand-700">{formatValue(after[key])}</span>
            ) : null}
          </div>
        );
      })}
      {detail.map(([key, value]) => (
        <div key={key} className="flex flex-wrap items-baseline gap-x-2 text-xs">
          <span className="text-ink-muted">{fieldLabel(key)}</span>
          <span className="font-mono text-ink">{formatValue(value)}</span>
        </div>
      ))}
    </div>
  );
}

export function AuditLogTable({ logs }: { logs: AuditLogEntry[] }) {
  return (
    <TableWrap>
      <Table>
        <thead>
          <tr>
            <Th className="w-40">DATE</Th>
            <Th className="w-48">ACTOR</Th>
            <Th className="w-56">ACTION</Th>
            <Th className="w-56">TARGET</Th>
            <Th>CHANGES</Th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => {
            const target = targetOf(log);
            return (
              <tr key={log.id}>
                <Td className="whitespace-nowrap font-mono text-xs text-ink-muted">
                  {timestamp(log.createdAt)}
                </Td>
                <Td className="text-sm">
                  <span className="flex items-center gap-1.5">
                    <span className="truncate">{log.actorLabel}</span>
                    {/* 運営はテナントの利用者ではない。混ぜて読ませない。 */}
                    {log.actorType === 'operator' ? (
                      <Badge variant="outline">{'運営'}</Badge>
                    ) : null}
                  </span>
                </Td>
                <Td className="text-sm">{actionLabel(log.action)}</Td>
                <Td className="text-sm">
                  {log.targetLabel === null ? (
                    <span className="text-ink-subtle">{'—'}</span>
                  ) : target === null ? (
                    <span className="truncate">{log.targetLabel}</span>
                  ) : (
                    <EntityLink target={target} className="truncate">
                      {log.targetLabel}
                    </EntityLink>
                  )}
                </Td>
                <Td>
                  <Changes changes={log.changes} />
                </Td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </TableWrap>
  );
}

/** 絞り込みの照合に使う項目。操作者・操作・対象の 3 つ。 */
export function auditLogFields(log: AuditLogEntry): Array<string | null> {
  return [log.actorLabel, log.action, actionLabel(log.action), log.targetLabel];
}
